/**
 * Create Action function for Reflux replacement.
 * Supports simple actions, async actions with children, and custom action definitions.
 *
 * @module reflux/createAction
 */

import { createPublisher } from './publisher.js';
import { logAction, isEnabled } from './monitoring.js';
import {
  DEFAULT_ASYNC_RESULTS,
  ACTION_RESULT_COMPLETE,
  ACTION_RESULT_FAIL,
  ACTION_RESULT_COMPLETED,
  ACTION_RESULT_FAILED,
} from './constants.js';

/**
 * Creates child actions from children array.
 * Each child action is namespaced with the parent action name.
 *
 * @param {string[]} children - Child action names (e.g., ['complete', 'fail'])
 * @param {string} parentName - Parent action name for namespacing
 * @returns {Object} Object with child actions keyed by child name
 *
 * @example
 * const childActions = createChildActions(['complete', 'fail'], 'login');
 * // Returns: { complete: Action, fail: Action }
 * // Each action has actionName: 'login.complete', 'login.fail'
 */
const createChildActions = (children, parentName) => {
  const childActions = {};

  for (const childName of children) {
    const childAction = createAction({
      actionName: `${parentName}.${childName}`
    });

    childActions[childName] = childAction;
  }

  return childActions;
};

/**
 * Attaches child actions to parent action and adds legacy aliases.
 * Creates BOTH 'complete'/'completed' and 'fail'/'failed' aliases for
 * backward compatibility with Reflux v0.4.1.
 *
 * @param {Function & Partial<ActionProperties>} action - Parent action to attach children to
 * @param {Object.<string, Action>} childActions - Child actions keyed by name
 *
 * @example
 * const action = createAction('login');
 * const children = createChildActions(['complete', 'fail'], 'login');
 * attachChildActions(action, children);
 * // Now: action.complete === action.completed (same action, two names)
 */
const attachChildActions = (action, childActions) => {
  // Attach all child actions to parent
  Object.assign(action, childActions);

  // Add aliases for common child names (Reflux v0.4.1 compatibility)
  // Legacy Reflux creates BOTH 'complete' and 'completed' (same action, two names)
  // This allows either naming convention to work:
  //   - action.complete.listen(...) - modern style
  //   - action.completed.listen(...) - legacy style
  // Both point to the same action object to maintain backward compatibility
  if (childActions[ACTION_RESULT_COMPLETE]) {
    action[ACTION_RESULT_COMPLETED] = childActions[ACTION_RESULT_COMPLETE];
  }

  if (childActions[ACTION_RESULT_FAIL]) {
    action[ACTION_RESULT_FAILED] = childActions[ACTION_RESULT_FAIL];
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalizes definition input to object form.
 *
 * @param {string|Object} definition - Action name or configuration object
 * @returns {Object} Normalized definition object
 * @throws {TypeError} If definition is not a string or object
 */
const normalizeDefinition = (definition) => {
  if (typeof definition === 'string') {
    return { actionName: definition };
  }
  if (typeof definition === 'object' && definition !== null) {
    return definition;
  }
  throw new TypeError('createAction: definition must be string or object');
};

/**
 * Validates children array configuration.
 *
 * @param {Array} children - Children array to validate
 * @throws {Error} If children is not an array or contains non-strings
 */
const validateChildren = (children) => {
  if (!Array.isArray(children)) {
    throw new Error('createAction: children must be an array');
  }
  for (const child of children) {
    if (typeof child !== 'string') {
      throw new Error('createAction: all children must be strings');
    }
  }
};

/**
 * Creates the listenAndPromise method for an action.
 * Useful for testing or waiting for action completion.
 * Note: Requires action.listen to be attached before calling.
 *
 * @param {Function & Partial<ActionProperties>} action - The action with listen method attached
 * @returns {Function} The listenAndPromise function
 */
const createListenAndPromise = (action) => {
  // biome-ignore lint: Regular function required for proper context binding
  return function(callback, bindContext) {
    bindContext = bindContext || action;

    return new Promise((resolve, reject) => {
      // biome-ignore lint: Regular function required to use .apply() for context binding
      const unlisten = action.listen(function(...args) {
        try {
          const result = callback.apply(bindContext, args);

          // Handle promise result
          if (result && typeof result.then === 'function') {
            result.then(resolve).catch(reject);
          } else {
            resolve(result);
          }

          unlisten();
        } catch (error) {
          reject(error);
          unlisten();
        }
      }, bindContext);
    });
  };
};

/**
 * Attaches publisher methods to an action.
 * Mutates the action function to add all ActionProperties.
 *
 * @param {Function & Partial<ActionProperties>} action - The action function to enhance
 * @param {Object} publisher - The publisher instance
 */
const attachPublisherMethods = (action, publisher) => {
  action.trigger = publisher.trigger.bind(publisher);
  action.triggerAsync = publisher.triggerAsync.bind(publisher);

  // Wrap listen to bind context to action (so `this.complete` works in handlers)
  // biome-ignore lint: Regular function required to use .call() for proper context binding
  action.listen = function(callback, bindContext) {
    return publisher.listen.call(publisher, callback, bindContext || action);
  };

  action.eventLabel = publisher.eventLabel;
  action.emitter = publisher.emitter;
  action.listenAndPromise = createListenAndPromise(action);
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * @typedef {Object} ActionDefinition
 * @property {string} [actionName] - Action name for debugging and memory tracking
 * @property {boolean} [asyncResult] - Whether action supports async operations
 * @property {string[]} [children] - Names of child actions (e.g., ['complete', 'fail'])
 */

/**
 * @typedef {Object} ActionProperties
 * @property {Function} trigger - Trigger event synchronously
 * @property {Function} triggerAsync - Trigger event asynchronously
 * @property {Function} listen - Subscribe to action events
 * @property {Function} listenAndPromise - Listen and return promise for first event
 * @property {string} eventLabel - Event name for the action
 * @property {import('eventemitter3').default} emitter - Underlying event emitter
 * @property {ActionDefinition} [definition] - Original definition object
 */

/**
 * Action type combining callable function with publisher properties.
 * Used for typing action parameters in helper functions.
 * @typedef {Function & ActionProperties} Action
 */

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Creates a single action.
 * Actions can be simple (just trigger/listen) or async (with complete/fail children).
 *
 * @param {string|ActionDefinition} definition - Action name string or configuration object
 * @returns {Function} Action function with trigger(), listen(), and optional child actions
 *
 * @example
 * // Simple action
 * const logout = createAction('logout');
 * logout(); // Triggers action
 * logout.listen((data) => console.log(data)); // Subscribe
 *
 * @example
 * // Async action with children
 * const login = createAction({
 *   asyncResult: true,
 *   children: ['complete', 'fail']
 * });
 *
 * login.listen(function(credentials) {
 *   api.login(credentials)
 *     .then(this.complete)
 *     .catch(this.fail);
 * });
 *
 * login.complete.listen((user) => console.log('Success:', user));
 * login.fail.listen((error) => console.error('Error:', error));
 */
export const createAction = (definition) => {
  const def = normalizeDefinition(definition);

  const actionName = def.actionName || 'action';
  const asyncResult = def.asyncResult === true;
  const children = def.children || (asyncResult ? DEFAULT_ASYNC_RESULTS : []);

  validateChildren(children);

  const publisher = createPublisher(actionName, actionName, 'action');

  // biome-ignore lint: Regular function needed for consistency with other action methods
  const action = function(...args) {
    if (isEnabled()) logAction(actionName, action);
    publisher.triggerAsync(...args);
  };

  attachPublisherMethods(action, publisher);

  if (asyncResult || children.length > 0) {
    const childActions = createChildActions(children, actionName);
    attachChildActions(action, childActions);
  }

  action.definition = def;
  return action;
};
